import { T } from 'gt-react';
import { Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { FlowArrowIcon } from '@phosphor-icons/react';
import { Action, Subject } from '@erp/authz';
import { orpc } from 'src/api/rpc/client';
import { useCan } from 'src/hooks/useCan';
import { Badge } from 'src/components/ui/badge';
import { TaskTypeSection } from './TaskTypeEditorSections';
import { taskTypeFeedsView } from './taskTypeFeedsView';

/**
 * The workflows that use this task type: each has a step whose config names
 * the type. Read-only; workflows are built in the workflow editor.
 */
export function TaskTypeFeedsSection({ taskTypeKey }: { taskTypeKey: string }) {
  const feedsQuery = orpc.taskTypes.listFeeds.queryOptions({
    input: { taskTypeKey },
  });
  const [canReadWorkflows] = useCan(Action.Read, Subject.Workflow);
  const {
    data: feeds = [],
    isLoading,
    isError,
  } = useQuery({ ...feedsQuery, enabled: canReadWorkflows });
  const view = taskTypeFeedsView({
    canReadWorkflows,
    isLoading,
    isError,
    feedCount: feeds.length,
  });

  return (
    <TaskTypeSection
      title={<T context="task type editor section title">Linked workflows</T>}
      description={
        <T context="task type editor linked workflows section description">
          Workflows that use this task type.
        </T>
      }
    >
      {view === 'list' ? (
        <ul className="flex flex-col gap-2">
          {feeds.map((feed) => (
            <li key={feed.workflowId} className="flex items-center gap-2">
              <FlowArrowIcon className="h-4 w-4 text-muted-foreground" />
              <Link
                to="/workflows/$workflowId"
                params={{ workflowId: feed.workflowId }}
                className="text-sm underline underline-offset-2"
              >
                {feed.name}
              </Link>
              <Badge variant={feed.isActive ? 'default' : 'secondary'}>
                {feed.isActive ? (
                  <T context="badge label for an active workflow">Active</T>
                ) : (
                  <T context="badge label for an inactive workflow">Inactive</T>
                )}
              </Badge>
            </li>
          ))}
        </ul>
      ) : null}
      {view === 'empty' ? (
        <p className="text-sm text-muted-foreground">
          <T context="task type with no linked workflow">
            No workflow uses this type yet.
          </T>
        </p>
      ) : null}
      {view === 'error' ? (
        <p className="text-sm text-destructive">
          <T context="task type linked workflows failed to load">
            Couldn't load the linked workflows.
          </T>
        </p>
      ) : null}
      {view === 'no-access' ? (
        <p className="text-sm text-muted-foreground">
          <T context="task type linked workflows hidden for lack of workflow access">
            You need access to workflows to see this type's linked workflows.
          </T>
        </p>
      ) : null}
    </TaskTypeSection>
  );
}
